import { defineStore } from 'pinia'
import { AxiosError } from 'axios'
import * as Sentry from '@sentry/vue'
import i18next from '@/common/i18n'
import {
    login,
    logout,
    requestPasswordResetPin,
    getOrganizerByCurrentUser,
    getParticipantByUserId,
    updateOrganizerByDocumentId,
    refreshToken as refreshTokenApi,
} from '@/api/authApi'
import { getCurrentUser, updateUserById } from '@/api/users'
import { isServerError } from '@/utils/errorhandler'
import { useErrorPageStore } from '@/stores/error-page.store'
import {
    USER_MUTABLE_FIELDS,
    ORGANIZER_MUTABLE_FIELDS,
    type User,
    type Organizer,
    type Participant,
    UserRole,
    type OrganizerStrapiResponse,
} from '@/types/users'
import router from '@/router/router'
import { useToastStore } from '@/stores/toast.store'
import { computed, ref } from 'vue'

// Module-level widget handle so repeated fetchUser() calls don't append duplicate widgets
let feedbackWidget:
    ReturnType<NonNullable<ReturnType<typeof Sentry.getFeedback>>['createWidget']> | undefined

export const useAuthStore = defineStore('auth', () => {
    const accessToken = ref<string | null>(null)
    const user = ref<User | null>(null)
    const organizer = ref<OrganizerStrapiResponse | null>(null)
    const participant = ref<Participant | null>(null)

    function setJwt(token: string) {
        accessToken.value = token
    }
    const isOrganizer = computed(() => user.value?.role?.type === 'organizer')
    const isReviewer = computed(() => user.value?.role?.type === 'reviewer')
    const isParticipant = computed(() => user.value?.role?.type === 'participant')

    const { showToast } = useToastStore()

    function clearSessionState() {
        accessToken.value = null
        user.value = null
        organizer.value = null
        participant.value = null
        Sentry.setUser(null)
        Sentry.getFeedback()?.remove()
        feedbackWidget = undefined
    }

    async function refreshToken() {
        try {
            const result = await refreshTokenApi()
            if (result) {
                setJwt(result.jwt)
            } else {
                accessToken.value = null
            }
        } catch (err) {
            accessToken.value = null
            // WHY: An unreachable or failing CMS breaks the whole app (incl. the NavBar),
            // so show only a fullscreen 500 page in place.
            if (isServerError(err)) useErrorPageStore().showError(500, { fullscreen: true })
        }
    }

    async function init(): Promise<void> {
        await refreshToken()
        if (!accessToken.value) return
        await fetchUser()
    }

    async function loginUser(
        identifier: string,
        password: string,
        rememberMe = false
    ): Promise<boolean> {
        await login(identifier, password, rememberMe)

        await init()
        if (!accessToken.value) {
            throw new Error('Login failed: no access token after refresh.')
        }

        return true
    }

    async function logoutUser(): Promise<void> {
        try {
            await logout()
        } catch (err) {
            throw new Error(
                'Logout failed: ' + (err instanceof Error ? err.message : 'Unknown error'),
                { cause: err }
            )
        }

        clearSessionState()

        const current = router.currentRoute.value
        const onLogin = current.name === 'login'
        const target = !onLogin ? current.fullPath : undefined

        router.replace({
            name: 'login',
            query: target ? { redirect: target } : undefined,
        })
    }

    async function fetchUser(): Promise<void> {
        if (!accessToken.value) throw new Error('Not logged in. No JWT present.')
        try {
            user.value = await getCurrentUser()
            const roleType = user.value.role?.type
            // After loading the user, load the related role-specific profile data the application needs.
            if (roleType === 'organizer') {
                organizer.value = await getOrganizerByCurrentUser()
            } else if (roleType === 'participant') {
                participant.value = await getParticipantByUserId(Number(user.value.id))
            }
            // Attach user ID to Sentry events — no email, name, or other PII
            Sentry.setUser({ id: String(user.value.id), role: user.value.role?.type })
            // Show the feedback widget; remove any existing one first to avoid duplicates
            feedbackWidget?.removeFromDom()
            feedbackWidget = Sentry.getFeedback()?.createWidget()
        } catch (err: unknown) {
            if (err instanceof AxiosError) {
                const status = err.response?.status
                if (status === 401 || status === 403) {
                    await logoutUser()
                }
            }
            throw err
        }
    }

    async function refreshParticipantData(): Promise<void> {
        if (!user.value) throw new Error('Not logged in. No user found.')
        try {
            participant.value = await getParticipantByUserId(Number(user.value.id))
        } catch (err: unknown) {
            if (err instanceof AxiosError) {
                const status = err.response?.status
                if (status === 401 || status === 403) {
                    await logoutUser()
                }
            }
            throw err
        }
    }

    function buildSubset<T extends object, K extends readonly (keyof T)[]>(
        src: Partial<T>,
        keys: K
    ): Partial<Pick<T, K[number]>> {
        const out: Partial<Pick<T, K[number]>> = {}
        for (const k of keys) {
            const v = src[k]
            if (v !== undefined) {
                out[k] = v
            }
        }
        return out
    }

    async function updateGeneric(
        updateData: Partial<User> & Partial<Organizer>,
        role?: UserRole
    ): Promise<void> {
        if (!user.value) throw new Error('Not logged in. No user found.')
        const userId = user.value.id
        const roleType = user.value.role?.type
        const effectiveRole: UserRole =
            role ??
            (roleType === 'organizer'
                ? UserRole.Organizer
                : roleType === 'participant'
                  ? UserRole.Participant
                  : UserRole.User)

        const userPayload = buildSubset<User, typeof USER_MUTABLE_FIELDS>(
            updateData,
            USER_MUTABLE_FIELDS
        )

        const handlers: Record<UserRole, () => Promise<void>> = {
            [UserRole.User]: async () => {
                if (Object.keys(userPayload).length) {
                    await updateUserById(userId, userPayload)
                }
            },
            [UserRole.Organizer]: async () => {
                if (Object.keys(userPayload).length) {
                    await updateUserById(userId, userPayload)
                }
                const organizerPayload = buildSubset<Organizer, typeof ORGANIZER_MUTABLE_FIELDS>(
                    updateData,
                    ORGANIZER_MUTABLE_FIELDS
                )
                if (Object.keys(organizerPayload).length) {
                    const organizerEntry = await getOrganizerByCurrentUser()
                    if (organizerEntry) {
                        const documentId = organizerEntry.documentId
                        // Transform company to documentId if it's an object
                        const apiPayload = {
                            ...organizerPayload,
                            ...(organizerPayload.company &&
                            typeof organizerPayload.company === 'object'
                                ? { company: organizerPayload.company.documentId }
                                : {}),
                        }
                        await updateOrganizerByDocumentId(documentId, { data: apiPayload })
                    }
                }
            },
            [UserRole.Participant]: async () => {
                if (Object.keys(userPayload).length) {
                    await updateUserById(userId, userPayload)
                }
            },
        }

        await handlers[effectiveRole]()

        try {
            await fetchUser()
        } catch {
            showToast({
                severity: 'error',
                summary: i18next.t('frontend.messages.profile.updateFetchFailed'),
            })
            throw new Error('Failed to fetch updated user data.')
        }

        showToast({
            severity: 'success',
            summary: i18next.t('frontend.messages.profile.updateSuccess'),
        })
    }

    async function requestPasswordReset(email: string) {
        try {
            await requestPasswordResetPin(email)
            showToast({
                severity: 'success',
                summary: i18next.t('ui.passwordReset.requestSuccess'),
            })
        } catch (err) {
            showToast({
                severity: 'error',
                summary: i18next.t('ui.passwordReset.requestFailed'),
            })
            throw err
        }
    }

    async function clearSession(): Promise<void> {
        clearSessionState()

        await router.replace({ name: 'login' })
    }

    return {
        accessToken,
        user,
        organizer,
        participant,
        isOrganizer,
        isReviewer,
        isParticipant,
        setJwt,
        init,
        loginUser,
        fetchUser,
        logoutUser,
        clearSession,
        requestPasswordReset,
        updateGeneric,
        refreshToken,
        refreshParticipantData,
    }
})
